import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import { Col } from '@cuemath/comps';
import COLORS from '../../constants/colors';
import {
  useGetEvalSpecialistAlternates,
  usePostEvalSpecialistReassign,
} from 'actions/eval-specialist-reassign';
import { isUuid } from './constants';

// Posting no new_teacher_id lets the manager auto-pick free_alternates[0].
const AUTO_PICK_VALUE = 'auto';

// The API layer throws {code, error: {message}}, so the string is nested — rendering
// the object itself shows nothing. Always surface the backend's message.
const errorMessage = error => {
  if (!error) {
    return null;
  }
  return typeof error === 'string' ? error : error.message || null;
};

const describeSpecialist = (name, teacherId) => {
  if (!teacherId) {
    return '-';
  }
  return name ? `${name} (${teacherId})` : teacherId;
};

const describeStudent = student => {
  const { name, grade, student_id: studentId } = student || {};
  if (!studentId) {
    return '-';
  }
  const label = [name, grade ? `Grade ${grade}` : null].filter(Boolean).join(' · ');
  return label ? `${label} (${studentId})` : studentId;
};

const ReassignSpecialist = ({ closeModal }) => {
  const [studentServiceIdInput, setStudentServiceIdInput] = React.useState('');
  const [lookedUpIds, setLookedUpIds] = React.useState(null);
  const [selectedTeacherId, setSelectedTeacherId] = React.useState(AUTO_PICK_VALUE);
  const [reassignError, setReassignError] = React.useState(null);
  const [reassignResult, setReassignResult] = React.useState(null);

  const {
    data: alternatesData,
    get: getAlternates,
    isProcessing: isAlternatesProcessing,
    error: alternatesError,
  } = useGetEvalSpecialistAlternates();

  const { post: postReassign, isProcessing: isReassigning } =
    usePostEvalSpecialistReassign({
      // Stay open on success: the AC needs to see the new demo id, not a modal
      // that closes on them with no confirmation.
      onComplete: (error, data) => {
        if (error) {
          setReassignError(errorMessage(error) || 'Reassignment failed');
          return;
        }
        setReassignError(null);
        setReassignResult(data || {});
      },
    });

  const studentServiceId = (studentServiceIdInput || '').trim();
  const isStudentServiceIdValid = isUuid(studentServiceId);

  const handleStudentServiceIdChange = React.useCallback((e, { value }) => {
    setStudentServiceIdInput(value);
    setLookedUpIds(null);
    setReassignError(null);
  }, []);

  const handleFetchAlternates = React.useCallback(() => {
    setSelectedTeacherId(AUTO_PICK_VALUE);
    setReassignError(null);
    setLookedUpIds({ studentServiceId });
    getAlternates(studentServiceId);
  }, [getAlternates, studentServiceId]);

  const {
    assessment_id: resolvedAssessmentId,
    current_teacher_id: currentTeacherId,
    current_teacher_name: currentTeacherName,
    student,
    slot_ist: slotIst,
    free_alternates: freeAlternates = [],
  } = alternatesData || {};

  const autoPick = freeAlternates[0];

  const options = React.useMemo(
    () => [
      {
        key: AUTO_PICK_VALUE,
        value: AUTO_PICK_VALUE,
        text: autoPick ? `Let the system choose (${autoPick.name})` : 'Let the system choose',
      },
      ...freeAlternates.map(alternate => ({
        key: alternate.teacher_id,
        value: alternate.teacher_id,
        text: alternate.name,
      })),
    ],
    [freeAlternates, autoPick],
  );

  const handleDropdownChange = React.useCallback((e, { value }) => {
    setSelectedTeacherId(value);
    setReassignError(null);
  }, []);

  const handleReassign = React.useCallback(() => {
    setReassignError(null);
    postReassign(
      {
        new_teacher_id: selectedTeacherId === AUTO_PICK_VALUE ? null : selectedTeacherId,
        action_source: 'ac',
      },
      lookedUpIds.studentServiceId,
    );
  }, [lookedUpIds, postReassign, selectedTeacherId]);

  // The POST returns ids only, so resolve the name from the list we already fetched.
  const newSpecialistName = reassignResult
    ? (freeAlternates.find(
        alternate => alternate.teacher_id === reassignResult.new_teacher_id,
      ) || {}).name
    : null;

  const hasAlternates = freeAlternates.length > 0;
  const hasBooking = Boolean(resolvedAssessmentId);
  const alternatesErrorMessage = errorMessage(alternatesError);
  // A lookup that returned an error has nothing to show, so don't fall through
  // to the details block with empty fields.
  const hasLookedUp = Boolean(lookedUpIds) && !alternatesErrorMessage;

  return (
    <Modal open={true} onClose={closeModal}>
      <ModalHeader>
        <Text variant="h6">Reassign Eval Specialist</Text>
        <Text variant="body3" color={COLORS.GRAY}>
          Move an eval booking to a different specialist. The slot time does not change.
        </Text>
      </ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          {reassignResult ? (
            <Segment>
              <Text variant="body1Medium" color={COLORS.DARK_GREEN}>
                <Icon name="check circle" /> Specialist reassigned. The slot time is
                unchanged and the parent has been told about the new specialist.
              </Text>
              <Separator height={16} transparent />
              <Text variant="body1Medium">Student</Text>
              <Text variant="body1Regular">{describeStudent(reassignResult.student)}</Text>
              <Separator height={12} transparent />
              <Text variant="body1Medium">Slot (unchanged)</Text>
              <Text variant="body1Regular">{reassignResult.slot_ist || slotIst || '-'}</Text>
              <Separator height={12} transparent />
              <Text variant="body1Medium">New Specialist</Text>
              <Text variant="body1Regular">
                {describeSpecialist(
                  reassignResult.new_teacher_name || newSpecialistName,
                  reassignResult.new_teacher_id,
                )}
              </Text>
              <Separator height={12} transparent />
              <Text variant="body1Medium">Previous Specialist</Text>
              <Text variant="body1Regular">
                {describeSpecialist(
                  reassignResult.old_teacher_name,
                  reassignResult.old_teacher_id,
                )}
              </Text>
            </Segment>
          ) : (
          <>
          <Col>
            <Text variant="body1Medium">Student Service ID</Text>
            <Separator height={4} transparent />
            <Input
              fluid
              placeholder="Paste the student service UUID"
              value={studentServiceIdInput}
              onChange={handleStudentServiceIdChange}
              aria-label="Student service ID"
            />
            <Separator height={12} transparent />
            <Button
              onClick={handleFetchAlternates}
              disabled={!isStudentServiceIdValid || isAlternatesProcessing}
              loading={isAlternatesProcessing}
              color="black"
            >
              Find Available Specialists
            </Button>
          </Col>
          <Separator height={16} transparent />
          {isAlternatesProcessing ? (
            <Loader active inline="centered">
              Loading available specialists...
            </Loader>
          ) : alternatesErrorMessage ? (
            <Segment>
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                <Icon name="info circle" /> {alternatesErrorMessage}
              </Text>
            </Segment>
          ) : hasLookedUp ? (
            <>
              <Col
                gutter={16}
                gap={16}
                style={{ background: COLORS.WILD_SAND, borderRadius: '8px' }}
              >
                {/* Shown because the AC no longer types it: it confirms which booking was resolved. */}
                <Text variant="body1Medium">Assessment ID</Text>
                <Text variant="body1Regular">{resolvedAssessmentId || '-'}</Text>
                <Separator height={16} transparent />
                <Text variant="body1Medium">Student</Text>
                <Text variant="body1Regular">{describeStudent(student)}</Text>
                <Separator height={16} transparent />
                <Text variant="body1Medium">Current Specialist</Text>
                <Text variant="body1Regular">
                  {describeSpecialist(currentTeacherName, currentTeacherId)}
                </Text>
                <Separator height={16} transparent />
                <Text variant="body1Medium">Slot (unchanged)</Text>
                <Text variant="body1Regular">{slotIst || '-'}</Text>
              </Col>
              <Separator height={16} transparent />
              {hasAlternates ? (
                <>
                  <Text variant="body1Medium">New Specialist</Text>
                  <Separator height={8} transparent />
                  <Dropdown
                    floating
                    fluid
                    selection
                    onChange={handleDropdownChange}
                    options={options}
                    value={selectedTeacherId}
                    selectOnBlur={false}
                    placeholder="Select a specialist"
                  />
                  <Separator height={8} transparent />
                  <Text variant="body3" color={COLORS.GRAY}>
                    Specialists are listed least-loaded first. The parent is told about the
                    new specialist and is never told the eval was cancelled.
                  </Text>
                </>
              ) : (
                <Segment>
                  <Text variant="body2" color={COLORS.RUSTY_RED}>
                    <Icon name="info circle" /> No specialist is free at this slot — this
                    needs a different time, not a different specialist.
                  </Text>
                </Segment>
              )}
            </>
          ) : null}
          </>
          )}
          {reassignError && (
            <>
              <Separator height={16} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {reassignError}
              </Text>
            </>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {reassignResult ? (
          <Button onClick={closeModal} color="black">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={closeModal} basic>
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              color="black"
              disabled={
                !hasLookedUp ||
                isAlternatesProcessing ||
                !hasBooking ||
                !hasAlternates
              }
              loading={isReassigning}
            >
              Reassign Specialist
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default ReassignSpecialist;
